import {useContext, useEffect, useRef, useState} from 'react';
import {create} from '@bufbuild/protobuf';
import {StoreContext} from "../components/StoreContextProvider";
import {PlayerBaySession_Client} from "../constants";
import {
  SetPlayerConfirmationSettingsRequestSchema, ConfirmationSettingsSchema
} from "../../generated/bay_session_shared_pb";
import ServiceClient from '../utils/ServiceClient';

type UseConfirmBeforeEachTurnResult = {
  confirmBeforeEachTurn: boolean, setConfirmBeforeEachTurn: (value: boolean) => Promise<boolean>, isLoading: boolean
};

/**
 * useConfirmBeforeEachTurn — Context reader / writer for the "Lock
 * account after each turn" toggle across both session backends.
 *
 * The toggle reflects the inverse of `accountSecurityBypassEnabled` on
 * the bay-session PlayerState stream — bypass=true means "skip per-turn
 * confirmation", so the toggle ("confirm before each turn") is
 * `!bypassEnabled`. The mapping is computed by
 * playerStateToUserSessionData against
 * SessionPlayer.confirmation.on_turn (commit c588a4d0).
 *
 * Bay-session writes go through SetPlayerConfirmationSettings and persist to
 * bay_session_players.confirm_on_turn. Legacy-fallback sessions have no bay
 * player JWT, so they use partner-service's confirmation-bypass RPCs and the
 * participant row that feeds the legacy kiosk stream. Routing strictly on
 * sessionBackend keeps each write aligned with the stream that owns its truth.
 */
export function useConfirmBeforeEachTurn(): UseConfirmBeforeEachTurnResult {
  const [isLoading, setIsLoading] = useState(false);
  const Context = useContext(StoreContext);
  const {
    confirmBeforeEachTurnEnabled, setConfirmBeforeEachTurnEnabled, user, session,
    bayPlayerJwt, sessionBackend
  } = Context;
  const authSessionToken = session?.token ?? '';
  const userId = user?.userId ?? session?.userId ?? 0n;
  const authSessionStart = session?.sessionStart ?? 0n;
  const stationId = BigInt(Context.stationId || 0);
  const ownerKey = sessionBackend === 'legacy'
    ? [sessionBackend, authSessionToken, userId, authSessionStart].join(':')
    : [sessionBackend, authSessionToken, userId, stationId, bayPlayerJwt].join(':');
  const currentOwnerKeyRef = useRef(ownerKey);
  const inFlightRef = useRef<symbol | null>(null);
  const mountedRef = useRef(true);
  currentOwnerKeyRef.current = ownerKey;

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      inFlightRef.current = null;
    };
  }, []);

  useEffect(() => {
    inFlightRef.current = null;
    setIsLoading(false);
  }, [ownerKey]);

  // SMG-3056: read on_turn directly from the dedicated Context field.
  // Previously this hook read !confirmationBypassEnabled, but after
  // SMG-3048 confirmationBypassEnabled tracks on_shot (the per-shot
  // bypass), NOT on_turn — so the "Lock account after each turn" toggle
  // was reflecting the wrong server flag.
  const confirmBeforeEachTurn = confirmBeforeEachTurnEnabled;

  const setConfirmBeforeEachTurn = async (value: boolean): Promise<boolean> => {
    const requestOwnerKey = ownerKey;
    if (currentOwnerKeyRef.current !== requestOwnerKey || inFlightRef.current) return false;

    const requestId = Symbol('confirm-before-each-turn-write');
    inFlightRef.current = requestId;
    try {
      setIsLoading(true);

      if (sessionBackend === 'legacy') {
        if (!authSessionToken) {
          throw new Error('Session not ready — please re-authenticate');
        }
        await ServiceClient.setLegacyConfirmBeforeEachTurn(value, authSessionToken);
        if (currentOwnerKeyRef.current !== requestOwnerKey || inFlightRef.current !== requestId) return false;
        setConfirmBeforeEachTurnEnabled(value);
        return true;
      }

      // bay-session SetPlayerConfirmationSettings needs station_id +
      // user_id + the settings struct. station_id comes from the
      // current PlayerState (the stream-driven userSessionData isn't
      // surfacing it directly, so we have to read it from the
      // PlayerState — for now we use the legacy
      // userSessionData.playMode-derived stationId via the kiosk
      // bridge; future BSA-4-x work threads currentStationId through
      // explicitly).
      //
      // The on_turn flag flips with `value`: toggle ON ↔ on_turn=true
      // ↔ bypass=false. on_shot is preserved at its prior value (the
      // toggle only owns on_turn).
      if (!bayPlayerJwt) {
        throw new Error('Bay-session player JWT missing — please re-authenticate');
      }
      if (stationId === 0n || userId === 0n) {
        throw new Error('Station / user not yet ready — try again in a moment');
      }

      const request = create(SetPlayerConfirmationSettingsRequestSchema, {
        stationId, userId,
        settings: create(ConfirmationSettingsSchema, {
          // value=true (toggle ON, "Lock after each turn") sets on_turn=true.
          onTurn: value
        })
      });
      await PlayerBaySession_Client.setPlayerConfirmationSettings(request, {headers: {Authorization: `Bearer ${bayPlayerJwt}`}});
      if (currentOwnerKeyRef.current !== requestOwnerKey || inFlightRef.current !== requestId) return false;

      // Commit to Context only after the API call resolves. On failure
      // we deliberately do NOT update Context — the bay-session stream
      // will eventually re-emit the truthy server state and reconcile
      // any skew between client desire and server reality.
      setConfirmBeforeEachTurnEnabled(value);
      return true;

    } catch (err: any) {
      if (currentOwnerKeyRef.current !== requestOwnerKey || inFlightRef.current !== requestId) return false;
      console.error('Failed to update confirm before each turn setting', err);

      if (err?.message) {
        Context.setCancelModalConfig({
          visible: true, title: 'ERROR', subtitle: err.message, rightBtnText: 'OK',
          onResume: () => {
            Context.setCancelModalConfig((prev) => ({...prev, visible: false}));
          },
          onClose: () => {
            Context.setCancelModalConfig((prev) => ({...prev, visible: false}));
          }
        });
      }

      return false;

    } finally {
      if (mountedRef.current && currentOwnerKeyRef.current === requestOwnerKey && inFlightRef.current === requestId) {
        inFlightRef.current = null;
        setIsLoading(false);
      }
    }
  };

  return {confirmBeforeEachTurn, isLoading, setConfirmBeforeEachTurn};
}
